import { StrictMode } from "react";
import { hydrateRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import {
  QueryClient,
  QueryClientProvider,
  HydrationBoundary,
} from "@tanstack/react-query";
import "./global.scss";
import App from "./App.tsx";
import i18n from "./i18n/config.ts";

// In production we don't want noisy console output.
// Keep console.warn/error for actionable diagnostics.
if (import.meta.env.PROD) {
  console.log = () => {};
  console.info = () => {};
  console.debug = () => {};

  // Temporary: suppress noisy production-only hydration mismatch logging (#418).
  // The site UI is still rendered; this avoids spamming console with an error
  // that we already mitigated at the SSR/asset level.
  const prevOnError = window.onerror;
  window.onerror = (...args: unknown[]) => {
    const message = args[0];
    if (
      typeof message === "string" &&
      message.includes("Minified React error #418")
    ) {
      return true; // prevents default browser error logging
    }

    if (typeof prevOnError === "function") {
      // Forward to previous handler (best-effort).
      return (prevOnError as (...innerArgs: unknown[]) => unknown)(...args) as
        | boolean
        | undefined;
    }

    return false;
  };
}

// Align i18n with server locale before hydration so first client render matches SSR (avoids #418)
const pathname = window.location.pathname;
const locale = pathname.startsWith("/ua")
  ? "uk"
  : pathname.startsWith("/en")
    ? "en"
    : pathname.startsWith("/pl")
      ? "pl"
      : pathname.startsWith("/ro")
        ? "ro"
        : pathname.startsWith("/hr")
          ? "hr"
          : "en";
i18n.language = locale;
if (i18n.options) i18n.options.lng = locale;
if (i18n.store) i18n.store.language = locale;

// Redirect from www to non-www
if (window.location.hostname.startsWith("www.")) {
  window.location.replace(
    window.location.protocol +
      "//" +
      window.location.hostname.substring(4) +
      window.location.pathname +
      window.location.search +
      window.location.hash,
  );
}

async function preloadTranslationsForClient(
  targetLocale: string,
): Promise<void> {
  try {
    const res = await fetch(`/locales/${targetLocale}/translation.json`, {
      cache: "force-cache",
    });
    if (!res.ok) return;
    const translations = await res.json();
    // Ensure the same translations are available for the first client render.
    i18n.addResourceBundle(
      targetLocale,
      "translation",
      translations,
      true,
      true,
    );
  } catch (e) {
    console.warn(
      `[i18n] Failed to preload translations for "${targetLocale}".`,
      e,
    );
  }
}

const init = async () => {
  await preloadTranslationsForClient(locale);

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 60 * 1000,
        retry: false,
      },
    },
  });

  const dehydratedState = (
    window as typeof window & { __REACT_QUERY_STATE__?: unknown }
  ).__REACT_QUERY_STATE__;

  hydrateRoot(
    document.getElementById("root")!,
    <StrictMode>
      <BrowserRouter>
        <QueryClientProvider client={queryClient}>
          <HydrationBoundary state={dehydratedState}>
            <App />
          </HydrationBoundary>
        </QueryClientProvider>
      </BrowserRouter>
    </StrictMode>,
  );

  // Remove loading class after hydration is complete
  requestAnimationFrame(() => {
    document.documentElement.classList.remove("loading");
  });
};

void init();
